import { ListItemIcon, ListItemText, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { DatePicker } from '@mui/x-date-pickers';
import type { ContractTerminationOptions } from '@vy/api-definition';
import { CONTRACT_TERMINATION_OPTIONS } from '@vy/api-definition';
import { DeleteCircle } from '@vy/ui-design-system';
import { isNullOrUndefined, raiseUnreachable, vyDate } from '@vy/utils';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { BaseSelect } from '../../../../../components/design/selects/BaseSelect';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useTerminateContractMutation } from '../../../../../hooks/requests/use-terminate-contract';
import { useDialog } from '../../../../../hooks/use-dialog';
import { runMutation } from '../../../../../utils/vy-mutation';

type MenuItemTerminateContractProps = {
  customerName: string;
  contractId: string;
  isActive: boolean;
  contractStartDate: Date;
  contractEndDate?: Date;
};

export const MenuItemTerminateContract = ({
  customerName,
  contractId,
  isActive,
  contractStartDate,
  contractEndDate,
}: MenuItemTerminateContractProps) => {
  const { handleClose, handleOpen, isDialogOpen } = useDialog();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(vyDate());
  const [selectedTerminationOption, setSelectedTerminationOption] = useState<ContractTerminationOptions>(
    CONTRACT_TERMINATION_OPTIONS.Immediate,
  );

  const terminateContractMutation = useTerminateContractMutation(contractId);

  const handleDialogClose = () => {
    if (!terminateContractMutation.isLoading) {
      handleClose();
    }
  };

  const handleDialogSubmit = async () => {
    const terminationDate = isNullOrUndefined(selectedDate) ? undefined : selectedDate.toDate();

    await runMutation(
      terminateContractMutation,
      {
        terminationOption: selectedTerminationOption,
        terminationDate:
          selectedTerminationOption === CONTRACT_TERMINATION_OPTIONS.Custom ? terminationDate : undefined,
      },
      {
        pending: 'Terminating...',
        success: 'Terminated',
        error: 'Failed to terminate contract',
      },
      {
        onSuccess: () => {
          handleClose();
        },
      },
    );
  };

  return (
    <div>
      <MenuItem onClick={handleOpen} disabled={!isActive}>
        <ListItemIcon>
          <DeleteCircle />
        </ListItemIcon>
        <ListItemText>Terminate</ListItemText>
      </MenuItem>
      <VyDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        label={`You are about to terminate ${customerName}'s contract`}
        labelDescription="This will terminate the contract and scheduled invoices"
        cancelButtonText="Cancel"
        ctaText="Confirm"
        ctaDisabled={terminateContractMutation.isLoading}
        onSubmit={handleDialogSubmit}
      >
        <Typography variant="h6" className="mb-2" style={{ fontSize: '12px' }}>
          Termination date
        </Typography>

        <BaseSelect
          disabled={terminateContractMutation.isLoading}
          fullWidth
          withNone={false}
          defaultValue={selectedTerminationOption}
          getLabelOption={(option) => getTerminationOptionLabel(option as ContractTerminationOptions)}
          values={Object.values(CONTRACT_TERMINATION_OPTIONS)}
          onChange={(event) => setSelectedTerminationOption(event.target.value as ContractTerminationOptions)}
        />

        {selectedTerminationOption === CONTRACT_TERMINATION_OPTIONS.Custom && (
          <div className="flex">
            <DatePicker
              className="my-6 grow"
              label="Choose"
              value={selectedDate}
              minDate={vyDate(contractStartDate).add(1, 'day')}
              maxDate={isNullOrUndefined(contractEndDate) ? undefined : vyDate(contractEndDate)}
              onChange={(date) => setSelectedDate(date)}
              sx={{
                '& .MuiInputBase-root': {
                  height: '47px',
                },
              }}
            />
          </div>
        )}
      </VyDialog>
    </div>
  );
};

function getTerminationOptionLabel(terminationOption: ContractTerminationOptions) {
  switch (terminationOption) {
    case CONTRACT_TERMINATION_OPTIONS.Immediate:
      return 'Immediate';
    case CONTRACT_TERMINATION_OPTIONS.Custom:
      return 'Custom';
    default:
      return raiseUnreachable(terminationOption);
  }
}
